import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getDashboardOverview } from 'src/services/admin/dashboard';

export interface DashboardState {
  overviewData: any;
  lastFetchTime: number | null;
  loading: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  overviewData: null,
  lastFetchTime: null,
  loading: false,
  error: null
};

// Get JST timezone date (Asia/Tokyo is UTC+9)
function getJSTDate(timestamp: number): Date {
  const date = new Date(timestamp);
  // Convert UTC time to JST (UTC+9) - Based on Japan time, not the time displayed in the browser
  const offset = (9 * 60 - date.getTimezoneOffset()) * 60 * 1000;
  return new Date(date.getTime() + offset);
}

export function getMsUntilNextJSTBoundary(): number {
  const now = Date.now();
  const nowJST = getJSTDate(now);

  const year = nowJST.getFullYear();
  const month = nowJST.getMonth();
  const day = nowJST.getDate();
  const hour = nowJST.getHours();

  const nextBoundaryJST =
    hour < 12
      ? new Date(year, month, day, 12, 0, 0, 0)
      : new Date(year, month, day + 1, 0, 0, 0, 0);

  // Reverse the shift applied by getJSTDate to get back a real timestamp
  const tzOffsetMin = new Date().getTimezoneOffset();
  const shift = (9 * 60 - tzOffsetMin) * 60 * 1000;
  const nextBoundaryTimestamp = nextBoundaryJST.getTime() - shift;

  return Math.max(nextBoundaryTimestamp - now, 0);
}

// Check if data needs refresh based on update boundaries
function shouldRefreshData(lastFetchTime: number | null): boolean {
  if (!lastFetchTime) return true;

  const now = Date.now();
  const lastFetchJST = getJSTDate(lastFetchTime);
  const nowJST = getJSTDate(now);

  // Get hour in JST
  const lastHour = lastFetchJST.getHours();
  const nowHour = nowJST.getHours();
  const lastDate = lastFetchJST.getDate();
  const nowDate = nowJST.getDate();

  // Different days → always refresh
  if (lastDate !== nowDate) return true;

  // Check if crossed 12:00 or 00:00 boundaries
  // If last fetch was before noon and now is after noon → refresh
  if (lastHour < 12 && nowHour >= 12) return true;

  // If last fetch was after noon and now is next day (or past midnight) → refresh
  if (lastHour >= 12 && (nowHour < 12 || nowDate !== lastDate)) return true;

  return false;
}

export const fetchDashboardOverview = createAsyncThunk(
  'dashboard/fetchOverview',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getDashboardOverview();
      return {
        data,
        fetchTime: Date.now()
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Failed to fetch dashboard overview'
      );
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardData: (state) => {
      state.overviewData = null;
      state.lastFetchTime = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
        state.overviewData = action.payload.data;
        state.lastFetchTime = action.payload.fetchTime;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchDashboardOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Keep old data if fetch fails, only clear on initial load
        if (!state.overviewData) {
          state.overviewData = null;
        }
      });
  }
});

export const { clearDashboardData } = dashboardSlice.actions;

// Selector to get data with auto-refresh logic
export const selectDashboardOverviewData = (state: any) => {
  const { overviewData, lastFetchTime } = state.dashboard;

  // Check if data needs refresh
  if (lastFetchTime && shouldRefreshData(lastFetchTime)) {
    return {
      data: overviewData,
      needsRefresh: true,
      lastFetchTime
    };
  }

  return {
    data: overviewData,
    needsRefresh: false,
    lastFetchTime
  };
};

export const selectDashboardLoading = (state: any) => state.dashboard.loading;
export const selectDashboardError = (state: any) => state.dashboard.error;
export const selectDashboardHasData = (state: any) =>
  state.dashboard.overviewData != null;

export default dashboardSlice;
